import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, ChevronRightIcon, PencilIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useMemo, useState } from 'react';

import Spinner from '../../../common/Spinner';
import type { SyndicFeedbackOverride } from '../ImportDetail.module';
import type { ManualTreatmentPdfClassification, ManualTreatmentPdfEntry } from '../manualTreatmentPdfClassification';

const TYPE_OPTIONS = [
  { value: 0, label: 'Ignoré' },
  { value: 1, label: 'Copropriété' },
  { value: 2, label: 'ASL' },
  { value: 4, label: 'AFUL' },
  { value: 3, label: 'Tertiaire' },
] as const;

const PDF_POINT_LABELS: Record<number, { title: string; description: string }> = {
  1: {
    title: 'Point 1 — Entrées ignorées du fichier',
    description: 'Entrées non rattachées à une copropriété du RNIC et non identifiées. Indiquez le type ou le numéro d\'immatriculation.',
  },
  2: {
    title: 'Point 2 — Copropriétés RNIC ignorées',
    description: 'Copropriétés présentes dans le RNIC mais absentes du fichier, que nous avons ignorées.',
  },
  3: {
    title: 'Point 3 — Copropriétés RNIC intégrées',
    description: 'Copropriétés présentes dans le RNIC mais absentes du fichier, que nous avons intégrées.',
  },
  4: {
    title: 'Point 4 — Ajustements effectués',
    description: 'Rapprochements manuels par nom ou adresse. Vérifiez la correspondance et l\'immatriculation.',
  },
  5: {
    title: 'Point 5 — ASL / AFUL / Tertiaire',
    description: 'Immeubles identifiés comme ASL, AFUL ou tertiaire. Vérifiez le statut attribué.',
  },
};

function resolveCurrentType(entry: ManualTreatmentPdfEntry, pdfPoint: number): number | undefined {
  if (entry.typeLabel) {
    const labelToType: Record<string, number> = {
      'Copropriété': 1,
      'ASL': 2,
      'Tertiaire': 3,
      'AFUL': 4,
      'Autre': 5,
    };
    return labelToType[entry.typeLabel];
  }
  if (pdfPoint === 1) {return 0;}
  if (pdfPoint === 2) {return 0;}
  if (pdfPoint === 3) {return 1;}
  if (pdfPoint === 4) {return 1;}
  return undefined;
}

interface SyndicFeedbackStepProps {
  classification: ManualTreatmentPdfClassification;
  overrides: SyndicFeedbackOverride[];
  onOverridesChange: (overrides: SyndicFeedbackOverride[]) => void;
  hasBeenApplied?: boolean;
  onApplyFeedbackClick?: () => void;
  applyingFeedback?: boolean;
  saving?: boolean;
}

interface EntryRowProps {
  entry: ManualTreatmentPdfEntry;
  pdfPoint: 1 | 2 | 3 | 4 | 5;
  currentType: number | undefined;
  overriddenType: number | undefined;
  overriddenImmat: string | undefined;
  isModified: boolean;
  onUpdateOverride: (key: string, pdfPoint: 1 | 2 | 3 | 4 | 5, field: 'newType' | 'newImmat', value: number | string | undefined, entry: ManualTreatmentPdfEntry) => void;
}

const FeedbackEntryRow: React.FC<EntryRowProps> = ({
  entry,
  pdfPoint,
  currentType,
  overriddenType,
  overriddenImmat,
  isModified,
  onUpdateOverride,
}) => {
  const [editingImmat, setEditingImmat] = useState(false);
  const [immatDraft, setImmatDraft] = useState('');

  const displayType = overriddenType ?? currentType;

  const handleTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = parseInt(e.target.value, 10);
    const newVal = val === currentType ? undefined : val;
    onUpdateOverride(entry.key, pdfPoint, 'newType', newVal, entry);
  }, [currentType, entry, onUpdateOverride, pdfPoint]);

  const handleStartImmatEdit = useCallback(() => {
    setImmatDraft(overriddenImmat ?? entry.immatriculation ?? '');
    setEditingImmat(true);
  }, [entry.immatriculation, overriddenImmat]);

  const handleConfirmImmat = useCallback(() => {
    const trimmed = immatDraft.trim();
    const original = entry.immatriculation?.trim() || '';
    const newVal = trimmed === original ? undefined : trimmed;
    onUpdateOverride(entry.key, pdfPoint, 'newImmat', newVal, entry);
    setEditingImmat(false);
  }, [entry, immatDraft, onUpdateOverride, pdfPoint]);

  const handleCancelImmat = useCallback(() => {
    setEditingImmat(false);
  }, []);

  const handleImmatKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {handleConfirmImmat();}
    if (e.key === 'Escape') {handleCancelImmat();}
  }, [handleCancelImmat, handleConfirmImmat]);

  const displayImmat = overriddenImmat ?? entry.immatriculation ?? '-';

  return (
    <div className={`grid grid-cols-[1fr_1fr_180px_200px] items-center gap-2 px-4 py-2.5 ${isModified ? 'bg-amber-50/60' : ''}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-800" title={entry.name}>{entry.name}</p>
        {entry.address && <p className="truncate text-[11px] text-gray-400" title={entry.address}>{entry.address}</p>}
      </div>

      <div className="min-w-0">
        <p className={`truncate text-sm ${overriddenImmat ? 'font-semibold text-amber-700' : 'text-gray-700'}`} title={displayImmat}>
          {displayImmat}
        </p>
        {overriddenImmat && (
          <p className="truncate text-[11px] text-gray-400 line-through" title={entry.immatriculation}>
            {entry.immatriculation || '-'}
          </p>
        )}
      </div>

      <div>
        <select
          value={displayType ?? ''}
          onChange={handleTypeChange}
          className={`w-full rounded border px-2 py-1.5 text-xs ${
            overriddenType !== undefined
              ? 'border-amber-300 bg-amber-50 font-semibold text-amber-700'
              : 'border-gray-200 bg-white text-gray-700'
          }`}
        >
          <option value="" disabled>--</option>
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-1">
        {editingImmat ? (
          <>
            <input
              type="text"
              value={immatDraft}
              onChange={(e) => setImmatDraft(e.target.value)}
              onKeyDown={handleImmatKeyDown}
              autoFocus
              className="w-full rounded border border-rgpd-blue-dark px-2 py-1.5 text-xs focus:ring-1 focus:ring-rgpd-blue-dark focus:outline-none"
            />
            <button onClick={handleConfirmImmat} className="rounded bg-green-600 px-2 py-1.5 text-[11px] font-semibold text-white hover:bg-green-700">OK</button>
            <button onClick={handleCancelImmat} className="rounded bg-gray-200 px-2 py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-300">X</button>
          </>
        ) : (
          <button
            onClick={handleStartImmatEdit}
            className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1.5 text-[11px] text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-800"
          >
            <PencilIcon className="h-3 w-3" />
            Modifier
          </button>
        )}
      </div>
    </div>
  );
};

interface SectionProps {
  pdfPoint: 1 | 2 | 3 | 4 | 5;
  entries: ManualTreatmentPdfEntry[];
  overrides: SyndicFeedbackOverride[];
  onUpdateOverride: (key: string, pdfPoint: 1 | 2 | 3 | 4 | 5, field: 'newType' | 'newImmat', value: number | string | undefined, entry: ManualTreatmentPdfEntry) => void;
}

const FeedbackSection: React.FC<SectionProps> = ({ pdfPoint, entries, overrides, onUpdateOverride }) => {
  const [expanded, setExpanded] = useState(true);
  const label = PDF_POINT_LABELS[pdfPoint];
  const overrideMap = useMemo(() => {
    const map = new Map<string, SyndicFeedbackOverride>();
    overrides.filter((o) => o.pdfPoint === pdfPoint).forEach((o) => map.set(o.key, o));
    return map;
  }, [overrides, pdfPoint]);

  const modifiedCount = useMemo(
    () => entries.filter((e) => overrideMap.has(e.key)).length,
    [entries, overrideMap],
  );

  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center gap-2 text-gray-500">
          <span className="text-xs font-semibold">Pt. {pdfPoint}</span>
          <span className="text-sm">{label.title}</span>
          <span className="ml-auto text-xs italic">Aucune entrée</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-3 rounded-t-lg bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
      >
        {expanded ? <ChevronDownIcon className="h-4 w-4 text-gray-500" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500" />}
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">{label.title}</p>
          <p className="text-[11px] text-gray-500">{label.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {modifiedCount > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              {modifiedCount} modifié{modifiedCount > 1 ? 's' : ''}
            </span>
          )}
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
            {entries.length}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="divide-y divide-gray-100">
          <div className="grid grid-cols-[1fr_1fr_180px_200px] gap-2 bg-gray-50 px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase">
            <span>Nom</span>
            <span>Immatriculation</span>
            <span>Type</span>
            <span>Nouvelle immat.</span>
          </div>
          {entries.map((entry) => {
            const override = overrideMap.get(entry.key);
            const currentType = resolveCurrentType(entry, pdfPoint);
            const hasTypeChange = override?.newType !== undefined;
            const hasImmatChange = override?.newImmat !== undefined;
            const isModified = hasTypeChange || hasImmatChange;

            return (
              <FeedbackEntryRow
                key={entry.key}
                entry={entry}
                pdfPoint={pdfPoint}
                currentType={currentType}
                overriddenType={override?.newType}
                overriddenImmat={override?.newImmat}
                isModified={isModified}
                onUpdateOverride={onUpdateOverride}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const SyndicFeedbackStep: React.FC<SyndicFeedbackStepProps> = ({
  classification,
  overrides,
  onOverridesChange,
  hasBeenApplied = false,
  onApplyFeedbackClick,
  applyingFeedback = false,
  saving = false,
}) => {
  const handleUpdateOverride = useCallback((
    key: string,
    pdfPoint: 1 | 2 | 3 | 4 | 5,
    field: 'newType' | 'newImmat',
    value: number | string | undefined,
    entry: ManualTreatmentPdfEntry,
  ) => {
    onOverridesChange(
      (() => {
        const existingIdx = overrides.findIndex((o) => o.key === key && o.pdfPoint === pdfPoint);
        const existing: SyndicFeedbackOverride = existingIdx >= 0
          ? { ...overrides[existingIdx] }
          : {
              key,
              pdfPoint,
              originalType: resolveCurrentType(entry, pdfPoint),
              originalImmat: entry.immatriculation,
            };

        if (field === 'newType') {
          existing.newType = value as number | undefined;
        } else {
          existing.newImmat = value as string | undefined;
        }

        const hasAnyChange = existing.newType !== undefined || existing.newImmat !== undefined;
        if (!hasAnyChange) {
          return overrides.filter((o) => !(o.key === key && o.pdfPoint === pdfPoint));
        }

        if (existingIdx >= 0) {
          const next = [...overrides];
          next[existingIdx] = existing;
          return next;
        }
        return [...overrides, existing];
      })(),
    );
  }, [overrides, onOverridesChange]);

  const sections: { pdfPoint: 1 | 2 | 3 | 4 | 5; entries: ManualTreatmentPdfEntry[] }[] = useMemo(() => [
    { pdfPoint: 1, entries: classification.ignoredFile },
    { pdfPoint: 2, entries: classification.ignoredRnic },
    { pdfPoint: 3, entries: classification.forcedRnic },
    { pdfPoint: 4, entries: classification.adjustments },
    { pdfPoint: 5, entries: classification.aslAfulTertiaire },
  ], [classification]);

  const totalOverrides = overrides.length;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <h3 className="text-base font-semibold text-amber-900">Retour syndic — Ajustements post-feedback</h3>
        <p className="mt-1 text-sm text-amber-800">
          Ajustez le type ou le numéro d'immatriculation des entrées en fonction du retour du syndic.
          Les 5 sections ci-dessous correspondent aux points 1 à 5 de la synthèse PDF envoyée.
        </p>
        {totalOverrides > 0 && (
          <p className="mt-2 text-xs font-semibold text-amber-700">
            {totalOverrides} modification{totalOverrides > 1 ? 's' : ''} en attente d'enregistrement.
          </p>
        )}
      </div>

      {sections.map(({ pdfPoint, entries }) => (
        <FeedbackSection
          key={pdfPoint}
          pdfPoint={pdfPoint}
          entries={entries}
          overrides={overrides}
          onUpdateOverride={handleUpdateOverride}
        />
      ))}

      {hasBeenApplied && onApplyFeedbackClick && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-green-900">
                L'import a déjà été appliqué.
              </p>
              <p className="mt-1 text-xs text-green-800">
                {totalOverrides > 0
                  ? `${totalOverrides} modification(s) en attente. Cliquez pour appliquer uniquement les différences aux copropriétés existantes.`
                  : 'Aucune modification en attente. Ajustez les entrées ci-dessus puis cliquez pour appliquer.'}
              </p>
            </div>
            <button
              onClick={onApplyFeedbackClick}
              disabled={applyingFeedback || saving || totalOverrides === 0}
              className="flex shrink-0 items-center gap-2 rounded bg-green-700 px-5 py-3 text-sm font-medium text-white transition-all hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {applyingFeedback ? (
                <>
                  <Spinner size="sm" />
                  Application en cours...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="h-5 w-5" />
                  Modifier le traitement basé sur ces retours
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyndicFeedbackStep;
